import { GlowFilter } from '@pixi/filter-glow';
import { captureException } from '@sentry/react';
import gsap from 'gsap';
import { PixiPlugin } from 'gsap/PixiPlugin';
import { Howl } from 'howler';
import * as PIXI from 'pixi.js';
import { assets } from '@/lib/assets';
import { loadTextureWithFallback } from '@/lib/utils';

// Register GSAP plugins
gsap.registerPlugin(PixiPlugin);
// Initialize PixiPlugin with PIXI namespace
PixiPlugin.registerPIXI(PIXI);

const notifyError = (message: string, error?: any) => {
  console.error(message, error);
  captureException(error || new Error(message));
};

// --------------------- GAME CONFIGURATION ------------------------

/**
 * PRIMARY GAME PARAMETERS
 * Adjust these values to change game difficulty and behavior
 */
const GAME_CONFIG = {
  INITIAL_TIME_LEFT: 60,
  // Core game mechanics
  INITIAL_TEMPERATURE: 50, // Starting temperature (0-100 scale)
  BUBBLES_FOR_SCORE: 10, // Number of bubble clicks before increasing player score

  // Temperature mechanics
  TEMPERATURE_DECREASE_RATE: 2.5, //, 1.9, // Base temperature decrease per second
  TEMPERATURE_DECREASE_RATE_ACCELERATION: 0.05, // How much decrease rate accelerates per 10 seconds
  MAX_TEMPERATURE_DECREASE_RATE: 4.0, // Maximum temperature decrease per second
  FLAME_TEMPERATURE_INCREASE: 5, // Temperature increase when clicking a flame
  MISSED_FLAME_PENALTY: 3, // Temperature penalty for missing a flame

  // Game thresholds
  GAME_OVER_TEMPERATURE_MIN: 0, // Game over if temperature below this
  GAME_OVER_TEMPERATURE_MAX: 100, // Game over if temperature above this
  TEMPERATURE_EXTREMELY_COLD: 15, // Threshold for "extremely cold" state
  TEMPERATURE_COLD: 30, // Threshold for "cold" state
  TEMPERATURE_HOT: 70, // Threshold for "hot" state

  // Spawning mechanics
  BUBBLE_SPAWN_RATE_ACCELERATION: 0.5, // Spawn rate increase per 10 seconds
  MAX_BUBBLE_SPAWN_RATE: 3.0, // Maximum spawns per second

  // Spawn positions (percentage of screen width)
  SPAWN_LEFT_MIN: 0.15,
  SPAWN_LEFT_MAX: 0.3,
  SPAWN_RIGHT_MIN: 0.7,
  SPAWN_RIGHT_MAX: 0.85,

  // Difficulty progression
  MAX_BUTTONS_ON_SCREEN: 8, // Maximum buttons that can appear simultaneously
  INITIAL_BUTTONS_ON_SCREEN: 5, // Starting number of buttons
  MAX_GAME_SPEED: 1.7, // Maximum game speed multiplier
};

// --------------------- Types and Interfaces ------------------------

// Button types
enum ButtonType {
  FLAME,
  DIAMOND,
}

// Game state
type gameStatus = 'idle' | 'playing' | 'paused' | 'gameover';

// Button configuration
interface ButtonConfig {
  type: ButtonType;
  x: number;
  y: number;
  speed: number;
}

// Button data extension for Sprite
interface ButtonData {
  type: ButtonType;
  speed: number;
  createdAt: number;
}

// Extend Sprite to include userData
interface ButtonSprite extends PIXI.Sprite {
  userData: ButtonData;
}

// Game callbacks
interface GameCallbacks {
  onGameStarted?: () => void;
  onScoreUpdated?: (score: number) => void;
  onTemperatureChanged?: (temp: number) => void;
  onGameOver?: (score: number, reason: 'frozen' | 'burned' | 'timeout') => void;
  onStatusChanged?: (status: gameStatus) => void;
  onTimeUpdated?: (timeLeft: number) => void;
}

// Particle textures interface
interface ParticleTextures {
  snow: PIXI.Texture;
  fireBack: PIXI.Texture[];
  fireMid: PIXI.Texture[];
  fireFront: PIXI.Texture[];
}

// --------------------- Game State Variables ------------------------

let app: PIXI.Application;
let buttonsContainer: PIXI.Container;
let status: gameStatus = 'idle';
let score = 0;
let temperature = GAME_CONFIG.INITIAL_TEMPERATURE;
let gameSpeed = 1;
let buttonSpawnTimer: ReturnType<typeof setTimeout>;
let activeButtons: ButtonSprite[] = [];
let gameStartTime = 0;
let maxButtonsOnScreen = GAME_CONFIG.INITIAL_BUTTONS_ON_SCREEN;
let bubbleClickCount = 0;
let diamondClickCount = 0;
let temperatureDecreaseTimer: ReturnType<typeof setTimeout>;
let gameTimeLeftInSec = GAME_CONFIG.INITIAL_TIME_LEFT; // 60 second countdown
let gameTimerInterval: ReturnType<typeof setInterval>;
let lastTimerUpdateTime = 0;
let lastDiamondSpawnTime = 0;

// Dimensions
let APP_WIDTH = 540;
let APP_HEIGHT = 960;

// --------------------- Asset Variables ------------------------

// Textures
let flameTexture: PIXI.Texture;
let diamondTexture: PIXI.Texture;

// Sound effects
let soundFlame: Howl;
let soundWin: Howl;
let soundFail: Howl;
let soundBurning: Howl;
let soundFreezing: Howl;

const getAllSounds = (): Howl[] => [
  soundFlame,
  soundWin,
  soundFail,
  soundBurning,
  soundFreezing,
];

// Particle system
let snowContainer: PIXI.ParticleContainer | null = null;
let fireBackContainer: PIXI.ParticleContainer | null = null;
let fireMidContainer: PIXI.ParticleContainer | null = null;
let fireFrontContainer: PIXI.ParticleContainer | null = null;

let snowParticles: PIXI.Sprite[] = [];
let fireBackParticles: PIXI.Sprite[] = [];
let fireMidParticles: PIXI.Sprite[] = [];
let fireFrontParticles: PIXI.Sprite[] = [];

const particleTextures: ParticleTextures = {
  snow: PIXI.Texture.EMPTY,
  fireBack: [],
  fireMid: [],
  fireFront: [],
};

// Callbacks
let onScoreUpdatedCallback: ((score: number) => void) | null = null;
let onTemperatureChangedCallback: ((temp: number) => void) | null = null;
let onGameStartedCallback: (() => void) | null = null;
let onGameOverCallback:
  | ((score: number, reason: 'frozen' | 'burned' | 'timeout') => void)
  | null = null;
let onStatusChangedCallback: ((status: gameStatus) => void) | null = null;
let onTimeUpdatedCallback: ((timeLeft: number) => void) | null = null;

// --------------------- Animation Helpers ------------------------

// Safe animation helper for scale animations - mobile compatible
const safeAnimateScale = (
  sprite: PIXI.DisplayObject,
  targetScale: number,
  duration = 0.3
) => {
  if (!sprite || sprite.destroyed) return;

  const originalScale = sprite.scale.x;
  const startTime = Date.now();

  const animate = () => {
    if (!sprite || sprite.destroyed) {
      app.ticker.remove(animate);
      return;
    }

    const elapsed = (Date.now() - startTime) / 1000;
    const progress = Math.min(1, elapsed / duration);
    const newScale = originalScale + (targetScale - originalScale) * progress;

    sprite.scale.set(newScale);

    if (progress >= 1) {
      app.ticker.remove(animate);
    }
  };

  app.ticker.add(animate);
};

// Safe animation helper for particle movement
const safeAnimateParticle = (
  particle: PIXI.DisplayObject,
  startX: number,
  startY: number,
  endX: number,
  endY: number,
  duration = 0.5
) => {
  if (!particle || particle.destroyed) return;

  const startTime = Date.now();
  const startAlpha = particle.alpha;

  const animate = () => {
    if (!particle || particle.destroyed) {
      app.ticker.remove(animate);
      return;
    }

    const elapsed = (Date.now() - startTime) / 1000;
    const progress = Math.min(1, elapsed / duration);

    particle.x = startX + (endX - startX) * progress;
    particle.y = startY + (endY - startY) * progress;
    particle.alpha = startAlpha * (1 - progress);

    if (progress >= 1) {
      app.ticker.remove(animate);
      if (particle && !particle.destroyed && particle.parent) {
        particle.parent.removeChild(particle);
        particle.destroy();
      }
    }
  };

  app.ticker.add(animate);
};

// --------------------- Core Game Functions ------------------------

// Initialize the game application
const initApp = async (canvas: HTMLCanvasElement): Promise<void> => {
  // Reset game state
  temperature = GAME_CONFIG.INITIAL_TEMPERATURE;
  score = 0;
  bubbleClickCount = 0;
  diamondClickCount = 0;
  activeButtons = [];
  gameSpeed = 1;
  gameTimeLeftInSec = GAME_CONFIG.INITIAL_TIME_LEFT;

  // Check if PIXI is already initialized
  if (app && app.stage) {
    console.log('App already initialized, reusing existing instance');
    return;
  }

  // Clean up previous instance if needed
  if (app) {
    console.log('Cleaning up previous instance');
    try {
      app.destroy(true, true);
    } catch (err) {
      console.warn('Error cleaning up previous app:', err);
    }
  }

  // Set up canvas dimensions
  APP_WIDTH = window.innerWidth;
  APP_HEIGHT = window.innerHeight;

  // Add visibility change detection for mobile focus/unfocus
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  document.addEventListener('visibilitychange', handleVisibilityChange);

  try {
    // First, try to create PIXI application with WebGL
    console.log('Initializing PIXI with WebGL...');
    try {
      app = new PIXI.Application({
        width: window.innerWidth,
        height: window.innerHeight,
        view: canvas,
        backgroundAlpha: 0,
        antialias: true,
        resolution: window.devicePixelRatio || 1,
        forceCanvas: false, // Try WebGL first
        backgroundColor: 0x000000,
        autoDensity: true,
        powerPreference: 'default',
      });
    } catch (webglError) {
      console.warn(
        'WebGL renderer initialization failed, falling back to Canvas:',
        webglError
      );

      // Fall back to Canvas renderer if WebGL fails
      app = new PIXI.Application({
        width: window.innerWidth,
        height: window.innerHeight,
        view: canvas,
        backgroundAlpha: 0,
        antialias: true,
        resolution: Math.min(window.devicePixelRatio || 1, 1), // Limit resolution for canvas
        forceCanvas: true, // Force Canvas renderer
        backgroundColor: 0x000000,
        autoDensity: true,
      });
    }

    // Verify we have a valid PIXI Application
    if (!app || !app.stage || !app.renderer) {
      throw new Error('PIXI application initialization failed');
    }

    // Create containers and initialize
    buttonsContainer = new PIXI.Container();
    app.stage.addChild(buttonsContainer);

    // Initialize particle arrays
    snowParticles = [];
    fireBackParticles = [];
    fireMidParticles = [];
    fireFrontParticles = [];

    // Load textures and sounds with error handling
    await loadTextures();
    loadSounds();

    // Set initial temperature
    if (onTemperatureChangedCallback) {
      onTemperatureChangedCallback(temperature);
    }

    // Start the game loop
    app.ticker.add(gameLoop);
    setStatus('idle');

    console.log('HeaterGame initialization complete');
  } catch (error) {
    notifyError('Fatal error initializing game:', error);
  }
};

// Handle visibility changes (tab focus/unfocus, mobile app switching)
const handleVisibilityChange = () => {
  if (document.hidden) {
    // Pause app when hidden
    if (app && app.ticker) app.ticker.stop();

    // Pause sounds
    if (soundFreezing && soundFreezing.playing()) soundFreezing.pause();
    if (soundBurning && soundBurning.playing()) soundBurning.pause();

    // Pause GSAP animations
    gsap.globalTimeline.pause();
  } else {
    // Resume if we were playing
    if (app && app.ticker && status === 'playing') {
      app.ticker.start();
    }

    // Resume GSAP animations
    gsap.globalTimeline.resume();
  }
};

// Load sounds
const loadSounds = () => {
  console.log('Initializing sound effects...');

  soundWin = new Howl({
    src: [assets.game_heater.sounds.success],
    volume: 0.6,
  });

  soundFail = new Howl({
    src: [assets.game_heater.sounds.fail],
    volume: 0.6,
  });

  soundFlame = new Howl({
    src: [assets.game_heater.sounds.click],
    volume: 0.6,
  });

  soundBurning = new Howl({
    src: [assets.game_heater.sounds.burning],
    volume: 0.4,
  });

  soundFreezing = new Howl({
    src: [assets.game_heater.sounds.freezing],
    volume: 0.6,
  });
};

// Update external callbacks
const updateCallbacks = (callbacks: GameCallbacks): void => {
  onGameStartedCallback = callbacks.onGameStarted || null;
  onScoreUpdatedCallback = callbacks.onScoreUpdated || null;
  onTemperatureChangedCallback = callbacks.onTemperatureChanged || null;
  onGameOverCallback = callbacks.onGameOver || null;
  onStatusChangedCallback = callbacks.onStatusChanged || null;
  onTimeUpdatedCallback = callbacks.onTimeUpdated || null;
};

// Game loop
const gameLoop = (delta: number) => {
  if (!app || status !== 'playing') return;

  updateDifficulty();
  updateButtons(delta);
  checkTemperatureWarning();
  updateTemperatureEffects();
  updateParticles();
};

// Start the game
const startGame = () => {
  try {
    console.log('Starting game...');

    // Reset game state
    score = 0;
    bubbleClickCount = 0;
    diamondClickCount = 0;
    temperature = GAME_CONFIG.INITIAL_TEMPERATURE;
    gameSpeed = 1;
    maxButtonsOnScreen = GAME_CONFIG.INITIAL_BUTTONS_ON_SCREEN;
    gameStartTime = Date.now();
    lastDiamondSpawnTime = gameStartTime; // Initialize diamond spawn time to game start
    gameTimeLeftInSec = GAME_CONFIG.INITIAL_TIME_LEFT;

    // Clear existing game elements
    clearButtons();
    clearAllParticles();

    // Set status and notify callbacks
    setStatus('playing');

    if (onTemperatureChangedCallback) {
      onTemperatureChangedCallback(temperature);
    }

    if (onScoreUpdatedCallback) {
      onScoreUpdatedCallback(score);
    }

    if (onTimeUpdatedCallback) {
      onTimeUpdatedCallback(gameTimeLeftInSec);
    }

    // Start temperature decrease
    startTemperatureDecrease();

    // Start countdown timer
    startGameTimer();

    // Start spawning buttons if rendering is available
    if (buttonSpawnTimer) {
      clearTimeout(buttonSpawnTimer);
    }
    buttonSpawnTimer = setTimeout(spawnButtons, 1000);
    if (onGameStartedCallback) {
      onGameStartedCallback();
    }
    console.log('Game started successfully');
  } catch (error) {
    notifyError('Error starting heater game:', error);
  }
};

// Clean up and destroy the app
const destroyApp = () => {
  // Remove event listeners
  document.removeEventListener('visibilitychange', handleVisibilityChange);

  // Clear timers
  if (buttonSpawnTimer) {
    clearTimeout(buttonSpawnTimer);
    clearInterval(buttonSpawnTimer);
    buttonSpawnTimer = null as unknown as ReturnType<typeof setTimeout>;
  }

  if (gameTimerInterval) {
    clearInterval(gameTimerInterval);
    gameTimerInterval = null as unknown as ReturnType<typeof setInterval>;
  }

  stopTemperatureDecrease();

  // Stop all sounds
  getAllSounds().forEach((sound) => {
    if (sound) sound.stop();
  });

  // Clear game elements
  clearButtons();
  clearAllParticles();

  // Destroy PIXI application
  if (app && app.stage) {
    app.ticker.stop();
    app.stop();
    app.destroy(true, true);
    app = null as unknown as PIXI.Application;
    buttonsContainer = null as unknown as PIXI.Container;
  }

  // Reset game state
  status = 'idle';
  score = 0;
  temperature = GAME_CONFIG.INITIAL_TEMPERATURE;
  gameSpeed = 1;
  activeButtons = [];
};

// --------------------- Temperature Management ------------------------

// Calculate dynamic temperature decrease rate based on time
const calculateTemperatureDecreaseRate = () => {
  const elapsedSeconds = (Date.now() - gameStartTime) / 1000;

  // Apply acceleration to base rate
  const acceleratedRate =
    GAME_CONFIG.TEMPERATURE_DECREASE_RATE +
    (elapsedSeconds / 10) * GAME_CONFIG.TEMPERATURE_DECREASE_RATE_ACCELERATION;

  // Return capped rate
  return Math.min(acceleratedRate, GAME_CONFIG.MAX_TEMPERATURE_DECREASE_RATE);
};

// Start automatic temperature decrease
const startTemperatureDecrease = () => {
  if (temperatureDecreaseTimer) {
    clearInterval(temperatureDecreaseTimer);
  }

  temperatureDecreaseTimer = setInterval(() => {
    if (status === 'playing') {
      const currentDecreaseRate = calculateTemperatureDecreaseRate();
      adjustTemperature(-currentDecreaseRate);
    }
  }, 1000); // Decrease temperature every second
};

// Stop temperature decrease timer
const stopTemperatureDecrease = () => {
  if (temperatureDecreaseTimer) {
    clearInterval(temperatureDecreaseTimer);
    temperatureDecreaseTimer = null as unknown as ReturnType<typeof setTimeout>;
  }
};

// Adjust temperature and check for game over
const adjustTemperature = (amount: number) => {
  temperature = Math.max(0, Math.min(100, temperature + amount));

  if (onTemperatureChangedCallback) {
    onTemperatureChangedCallback(temperature);
  }

  // Check for game over conditions
  if (
    temperature <= GAME_CONFIG.GAME_OVER_TEMPERATURE_MIN ||
    temperature >= GAME_CONFIG.GAME_OVER_TEMPERATURE_MAX
  ) {
    endGame(
      temperature <= GAME_CONFIG.GAME_OVER_TEMPERATURE_MIN ? 'frozen' : 'burned'
    );
  }
};

// Check temperature warning state
const checkTemperatureWarning = () => {
  // play danger zone
  const isBurning = temperature >= GAME_CONFIG.TEMPERATURE_HOT;
  const isFreezing = temperature <= GAME_CONFIG.TEMPERATURE_COLD;

  if (isFreezing) {
    if (!soundFreezing.playing()) soundFreezing.play();
    soundBurning?.stop();
  } else if (isBurning) {
    if (!soundBurning.playing()) soundBurning.play();
    soundFreezing?.stop();
  } else {
    soundFreezing?.stop();
    soundBurning?.stop();
  }

  // Check for extreme temperature
  if (
    (temperature <= GAME_CONFIG.GAME_OVER_TEMPERATURE_MIN ||
      temperature >= GAME_CONFIG.GAME_OVER_TEMPERATURE_MAX) &&
    status === 'playing'
  ) {
    handleGameOver();
  }
};

// Stop danger sound
const stopDangerSound = () => {
  soundFreezing.stop();
  soundBurning.stop();
};

// Convert internal temperature for display
const convertTemperatureForDisplay = (internalTemp: number): number => {
  return internalTemp; // Simply return the 0-100 value for filling the thermometer
};

// --------------------- Game State Management ------------------------

// Update game difficulty based on elapsed time
const updateDifficulty = () => {
  if (status !== 'playing') return;

  const elapsedSeconds = (Date.now() - gameStartTime) / 1000;

  // Update game speed
  if (elapsedSeconds < 20) {
    // First 20 seconds: 1.0 to 1.3 speed
    gameSpeed = 1 + (elapsedSeconds / 20) * 0.3;
  } else if (elapsedSeconds < 50) {
    // 20-50 seconds: 1.3 to 1.7 speed
    gameSpeed = 1.3 + ((elapsedSeconds - 20) / 30) * 0.4;
  } else {
    // After 50 seconds: maximum speed
    gameSpeed = GAME_CONFIG.MAX_GAME_SPEED;
  }

  // Update max buttons on screen
  if (elapsedSeconds < 20) {
    // First 20 seconds: 3 to 5 buttons
    maxButtonsOnScreen =
      GAME_CONFIG.INITIAL_BUTTONS_ON_SCREEN +
      Math.floor((elapsedSeconds / 20) * 2);
  } else if (elapsedSeconds < 50) {
    // 20-50 seconds: 5 to 7 buttons
    maxButtonsOnScreen = 5 + Math.floor(((elapsedSeconds - 20) / 30) * 2);
  } else {
    // After 50 seconds: maximum buttons
    maxButtonsOnScreen = GAME_CONFIG.MAX_BUTTONS_ON_SCREEN;
  }
};

// End the game
const endGame = (reason: 'frozen' | 'burned' | 'timeout') => {
  setStatus('gameover');
  stopTemperatureDecrease();
  stopDangerSound();

  // Clear timer
  if (gameTimerInterval) {
    clearInterval(gameTimerInterval);
  }

  if (onGameOverCallback) {
    onGameOverCallback(score, reason);
  }
};

// Set game status
const setStatus = (newStatus: gameStatus) => {
  status = newStatus;
  if (onStatusChangedCallback) {
    onStatusChangedCallback(status);
  }
};

// Get current game status
const getStatus = () => status;

// Update score
const updateScore = (newScore: number) => {
  score = newScore;
  if (onScoreUpdatedCallback) {
    onScoreUpdatedCallback(score);
  }
};

// Play success sound
const playSuccessSound = () => {
  if (soundWin) {
    soundWin.play();
  }
};

// Handle game over
const handleGameOver = () => {
  if (status === 'gameover') return;

  if (soundFail) {
    soundFail.play();
  }

  if (buttonSpawnTimer) {
    clearTimeout(buttonSpawnTimer);
    buttonSpawnTimer = null as unknown as ReturnType<typeof setTimeout>;
  }

  stopDangerSound();
  setStatus('gameover');

  if (onGameOverCallback) {
    onGameOverCallback(
      score,
      temperature <= GAME_CONFIG.GAME_OVER_TEMPERATURE_MIN ? 'frozen' : 'burned'
    );
  }
};

// --------------------- Button Management ------------------------

// Update button positions and states
const updateButtons = (delta: number) => {
  // Process each button
  for (let i = activeButtons.length - 1; i >= 0; i--) {
    const button = activeButtons[i];

    // Ensure button exists and has userData
    if (!button || !button.userData) continue;

    // Get elapsed time since button was created
    const buttonLifetime = Date.now() - button.userData.createdAt;

    // Apply speed based on game speed, elapsed time and delta
    const speed = button.userData.speed * gameSpeed * delta * 0.6;

    // Move buttons upward with gradually increasing speed
    const lifetimeFactor = Math.min(2, 1 + buttonLifetime / 2000);
    button.y -= speed * lifetimeFactor;

    // Only start fading buttons after they've been visible for at least 1 second
    if (buttonLifetime > 1200) {
      // Reduce opacity over time - faster fade as lifetime increases
      const fadeRate = 0.004 * (1 + (buttonLifetime - 1200) / 1500);
      button.alpha -= speed * fadeRate;
    }

    // Add slight pulsing effect
    const pulseSpeed = 0.004 + (button.x % 5) * 0.001;
    const pulseAmount = 0.03;
    const pulseScale = 1 + Math.sin(Date.now() * pulseSpeed) * pulseAmount;
    button.scale.set(0.35 * pulseScale);

    // Add horizontal movement after elapsed time
    const elapsedSeconds = (Date.now() - gameStartTime) / 1000;
    if (elapsedSeconds > 20) {
      const movementAmount = 0.15 + Math.min(0.5, (elapsedSeconds - 20) / 60);
      const waveOffset = Math.sin(buttonLifetime * 0.002) * movementAmount;
      button.x += waveOffset;
    }

    // Remove buttons that have faded out or are off-screen
    if (
      button.alpha <= 0.1 ||
      button.x < -50 ||
      button.x > APP_WIDTH + 50 ||
      button.y < -50 ||
      button.y > APP_HEIGHT + 50
    ) {
      // Button was missed
      handleMissedButton(button.userData.type);
      removeButton(button, i);
    }
  }
};

// Create a new button
const createButton = (config: ButtonConfig): ButtonSprite => {
  if (!buttonsContainer) {
    notifyError('Button container not initialized');
    return null as unknown as ButtonSprite;
  }

  const texture =
    config.type === ButtonType.DIAMOND ? diamondTexture : flameTexture;
  const button = new PIXI.Sprite(texture) as ButtonSprite;

  button.x = config.x;
  button.y = config.y;
  button.anchor.set(0.5);
  button.scale.set(0); // Start at 0
  button.alpha = config.type === ButtonType.DIAMOND ? 1 : 0.85;
  button.eventMode = 'static';
  button.cursor = 'pointer';

  button.userData = {
    type: config.type,
    speed: config.speed,
    createdAt: Date.now(),
  };

  const glowFilter = new GlowFilter({
    distance: 10,
    outerStrength: 1.5,
    innerStrength: 0.2,
    color: config.type === ButtonType.DIAMOND ? 0x00ffff : 0xff5500,
  });

  button.filters = [glowFilter];
  buttonsContainer.addChild(button);
  activeButtons.push(button);

  const targetScale = config.type === ButtonType.DIAMOND ? 0.4 : 0.35;

  // Simple scale animation
  gsap.fromTo(
    button.scale,
    { x: 0, y: 0 },
    {
      x: targetScale,
      y: targetScale,
      duration: 0.5,
      ease: 'elastic.out(1.2, 0.5)',
      onComplete: () => {
        if (button && !button.destroyed) {
          button.scale.set(targetScale);
        }
      },
    }
  );

  // Add glow animation
  const animateGlow = () => {
    if (!button || button.destroyed || !button.filters || !button.filters[0])
      return;

    const glowFilter = button.filters[0] as GlowFilter;
    gsap.to(glowFilter, {
      outerStrength: 2.2,
      duration: 1.2,
      ease: 'sine.inOut',
      yoyo: true,
      repeat: -1,
      repeatDelay: 0.3,
    });
  };

  setTimeout(animateGlow, 400);

  // Hover effects
  button.on('pointerover', () => {
    if (!button || button.destroyed) return;

    if (button.filters && button.filters[0]) {
      gsap.killTweensOf(button.filters[0]);
      const glowFilter = button.filters[0] as GlowFilter;
      glowFilter.outerStrength = 3;
      glowFilter.innerStrength = 0.5;
    }

    const hoverScale = config.type === ButtonType.DIAMOND ? 0.45 : 0.42;
    gsap.to(button.scale, {
      x: hoverScale,
      y: hoverScale,
      duration: 0.2,
      ease: 'back.out(2)',
      overwrite: true,
    });
  });

  button.on('pointerout', () => {
    if (!button || button.destroyed) return;

    gsap.to(button.scale, {
      x: targetScale,
      y: targetScale,
      duration: 0.2,
      ease: 'power2.out',
      overwrite: true,
    });

    if (button.filters && button.filters[0]) {
      const glowFilter = button.filters[0] as GlowFilter;
      glowFilter.outerStrength = 1.5;
      glowFilter.innerStrength = 0.2;
      animateGlow();
    }
  });

  button.on('pointerdown', () => {
    if (!button || button.destroyed) return;
    createExplosionEffect(button.x, button.y, button.userData.type);
    handleButtonClick(button);
  });

  return button;
};

// Remove a button from the game
const removeButton = (button: ButtonSprite, index: number) => {
  try {
    if (!button || button.destroyed) {
      if (index >= 0 && index < activeButtons.length) {
        activeButtons.splice(index, 1);
      }
      return;
    }

    if (!buttonsContainer) {
      if (index >= 0 && index < activeButtons.length) {
        activeButtons.splice(index, 1);
      }
      return;
    }

    if (buttonsContainer.children.includes(button)) {
      buttonsContainer.removeChild(button);
    }

    if (index >= 0 && index < activeButtons.length) {
      activeButtons.splice(index, 1);
    }

    if (!button.destroyed) {
      button.destroy();
    }
  } catch (err) {
    notifyError('Error removing button:', err);
    if (index >= 0 && index < activeButtons.length) {
      activeButtons.splice(index, 1);
    }
  }
};

// Handle button click
const handleButtonClick = (button: ButtonSprite) => {
  if (!button || button.destroyed || !button.userData || status !== 'playing')
    return;

  console.log('Button clicked:', button.userData.type);

  // Create explosion effect first
  createExplosionEffect(button.x, button.y, button.userData.type);

  // Play flame sound
  if (soundFlame) soundFlame.play();

  if (button.userData.type === ButtonType.FLAME) {
    // Handle flame button click
    adjustTemperature(GAME_CONFIG.FLAME_TEMPERATURE_INCREASE);
  }

  // Increment appropriate counter based on button type
  if (button.userData.type === ButtonType.DIAMOND) {
    diamondClickCount++;
    console.log('Diamond clicks:', diamondClickCount);
  } else {
    bubbleClickCount++;
    console.log(
      `Bubble clicks: ${bubbleClickCount}/${GAME_CONFIG.BUBBLES_FOR_SCORE}`
    );
  }

  // Calculate score based on both bubble clicks and diamonds
  const bubbleScore = Math.floor(
    bubbleClickCount / GAME_CONFIG.BUBBLES_FOR_SCORE
  );
  const totalScore = bubbleScore + diamondClickCount;

  // If the total score is different from our current score, update it
  if (totalScore !== score) {
    playSuccessSound();
    updateScore(totalScore);
  }

  // Remove the button
  const index = activeButtons.indexOf(button);
  if (index !== -1) {
    removeButton(button, index);
  } else if (buttonsContainer && !button.destroyed) {
    buttonsContainer.removeChild(button);
    button.destroy();
  }
};

// Handle missed button
const handleMissedButton = (_type: ButtonType) => {
  // Missing a flame button makes it colder
  adjustTemperature(-GAME_CONFIG.MISSED_FLAME_PENALTY);
};

// Clear all buttons
const clearButtons = () => {
  if (!buttonsContainer) {
    activeButtons = [];
    return;
  }

  for (const button of activeButtons) {
    buttonsContainer.removeChild(button);
    button.destroy();
  }
  activeButtons = [];
};

// Spawn buttons
const spawnButtons = () => {
  try {
    if (status !== 'playing' || !app || !buttonsContainer) return;

    // Only spawn a new button if we're under the maximum
    if (activeButtons.length >= maxButtonsOnScreen) {
      buttonSpawnTimer = setTimeout(spawnButtons, 300);
      return;
    }

    console.log('Spawning new button, active buttons:', activeButtons.length);

    // Check if it's time to spawn a diamond (every 10 seconds)
    const currentTime = Date.now();
    const shouldSpawnDiamond = currentTime - lastDiamondSpawnTime >= 10000;

    // Generate random position
    const leftSideSpawn = Math.random() < 0.5;

    let x;
    if (leftSideSpawn) {
      // Left side
      x =
        APP_WIDTH * GAME_CONFIG.SPAWN_LEFT_MIN +
        Math.random() *
          (APP_WIDTH *
            (GAME_CONFIG.SPAWN_LEFT_MAX - GAME_CONFIG.SPAWN_LEFT_MIN));
    } else {
      // Right side
      x =
        APP_WIDTH * GAME_CONFIG.SPAWN_RIGHT_MIN +
        Math.random() *
          (APP_WIDTH *
            (GAME_CONFIG.SPAWN_RIGHT_MAX - GAME_CONFIG.SPAWN_RIGHT_MIN));
    }

    // Position buttons in the bottom half of the screen
    const minY = APP_HEIGHT * 0.5;
    const maxY = APP_HEIGHT - 80;
    const y = minY + Math.random() * (maxY - minY);

    // Calculate speed
    const elapsedSeconds = (Date.now() - gameStartTime) / 1000;
    const baseSpeed = 0.7 + Math.min(1.5, elapsedSeconds / 50);
    const speed = baseSpeed * (1 + gameSpeed * 0.4);

    // Create the button with appropriate type
    const type = shouldSpawnDiamond ? ButtonType.DIAMOND : ButtonType.FLAME;
    const config = { type, x, y, speed };
    const button = createButton(config);

    if (!button) {
      notifyError('Failed to create button');
    }

    // Update last diamond spawn time if we spawned a diamond
    if (shouldSpawnDiamond) {
      lastDiamondSpawnTime = currentTime;
    }

    // Calculate spawn interval using BUBBLE_SPAWN_RATE_ACCELERATION
    const minSpawnInterval = 1000 / GAME_CONFIG.MAX_BUBBLE_SPAWN_RATE;
    const accelerationFactor =
      (elapsedSeconds / 10) * GAME_CONFIG.BUBBLE_SPAWN_RATE_ACCELERATION;
    const baseSpawnTime = Math.max(
      minSpawnInterval,
      2000 - accelerationFactor * 1000
    );

    // Apply game speed to spawn rate
    const nextSpawnTime = baseSpawnTime / gameSpeed;

    if (buttonSpawnTimer) {
      clearTimeout(buttonSpawnTimer);
    }

    if (status === 'playing') {
      buttonSpawnTimer = setTimeout(spawnButtons, nextSpawnTime);
    }
  } catch (error) {
    notifyError('Error spawning buttons:', error);
    if (status === 'playing') {
      buttonSpawnTimer = setTimeout(spawnButtons, 1000);
    }
  }
};

// --------------------- Effects ------------------------

// Create explosion effect
const createExplosionEffect = (x: number, y: number, type: ButtonType) => {
  if (!app) return;

  const particleCount = 30;
  const container = new PIXI.Container();
  app.stage.addChild(container);

  // Use blue for diamond, orange for flame
  const color = type === ButtonType.DIAMOND ? 0x00bfff : 0xff5500;
  const originalButtonSize = 40; // Approximate size of the original button

  // Create a main circle that scales up and fades out
  const mainCircle = new PIXI.Graphics();
  mainCircle.beginFill(color, 0.7);
  mainCircle.drawCircle(0, 0, originalButtonSize);
  mainCircle.endFill();
  mainCircle.x = x;
  mainCircle.y = y;
  mainCircle.alpha = 1;
  container.addChild(mainCircle);

  // Animate the main circle with scale and fade
  if (mainCircle && !mainCircle.destroyed) {
    safeAnimateScale(mainCircle, 2, 0.33);

    // Add separate animation for alpha fade
    const startTime = Date.now();
    const fadeDuration = 0.33;

    const fadeAnimate = () => {
      if (!mainCircle || mainCircle.destroyed) {
        app.ticker.remove(fadeAnimate);
        return;
      }

      const elapsed = (Date.now() - startTime) / 1000;
      const progress = Math.min(1, elapsed / fadeDuration);

      // Fade out
      mainCircle.alpha = 1 - progress;

      if (progress >= 1) {
        app.ticker.remove(fadeAnimate);

        // Clean up
        if (
          mainCircle &&
          !mainCircle.destroyed &&
          container.children.includes(mainCircle)
        ) {
          container.removeChild(mainCircle);
          mainCircle.destroy();
        }
      }
    };

    app.ticker.add(fadeAnimate);
  }

  // Create smaller particles that fly outward
  for (let i = 0; i < particleCount; i++) {
    const particle = new PIXI.Graphics();
    particle.beginFill(color);
    particle.drawCircle(0, 0, Math.random() * 5 + 3);
    particle.endFill();

    const angle = Math.random() * Math.PI * 2;
    const distance = 30 + Math.random() * 80;

    particle.x = x;
    particle.y = y;
    particle.alpha = 1;
    container.addChild(particle);

    // Calculate end position using angle and distance
    const endX = x + Math.cos(angle) * distance;
    const endY = y + Math.sin(angle) * distance;

    // Use our safe animation helper
    safeAnimateParticle(particle, x, y, endX, endY, 0.2 + Math.random() * 0.33);
  }
};

// Update game temperature effects
const updateTemperatureEffects = () => {
  // Calculate intensities
  const coldIntensity =
    temperature <= 50
      ? Math.min(1, Math.max(0, (50 - temperature) / (50 * 0.6)))
      : 0;

  const heatIntensity =
    temperature >= 50
      ? Math.min(1, Math.max(0, (temperature - 50) / (50 * 0.6)))
      : 0;

  // Increased probability of particle generation
  const coldProbability = coldIntensity * 0.45 + 0.1;
  const heatProbability = heatIntensity * 0.03 + 0.007;

  // Generate particles with probability based on intensity
  if (coldIntensity > 0 && Math.random() < coldProbability) {
    createSnowParticles(coldIntensity);
  }

  if (heatIntensity > 0 && Math.random() < heatProbability) {
    createFireParticles(heatIntensity);
  }

  // Update existing particles
  updateParticles();
};

// Create snow particles
const createSnowParticles = (intensity: number) => {
  try {
    // Enhanced null checks
    if (!app) {
      console.warn('Cannot create snow particles: app not available');
      return;
    }

    // Check if app is still valid (no direct 'destroyed' property)
    try {
      if (!app.stage || !app.renderer) {
        console.warn(
          'Cannot create snow particles: app components unavailable'
        );
        return;
      }
    } catch (err) {
      console.warn('Cannot create snow particles: app appears to be destroyed');
      return;
    }

    if (!snowContainer || snowContainer.destroyed) {
      console.warn(
        'Cannot create snow particles: snow container not available or destroyed'
      );
      return;
    }

    if (
      !particleTextures.snow ||
      particleTextures.snow === PIXI.Texture.EMPTY
    ) {
      console.warn('Cannot create snow particles: snow texture not available');
      return;
    }

    // Limit the number of snow particles
    const maxSnowParticles = 30;
    try {
      // Protect against null or undefined children array
      if (
        !snowContainer.children ||
        snowContainer.children.length > maxSnowParticles
      )
        return;
    } catch (err) {
      console.warn('Error checking snow container children:', err);
      return;
    }

    // Calculate probability based on temperature and intensity
    const probability =
      temperature <= 20
        ? 0.4
        : temperature <= 30
          ? 0.2
          : temperature <= 45
            ? 0.1
            : 0;

    // Skip based on probability
    if (Math.random() > probability * intensity) return;

    // Create snowflake
    const snowflake = new PIXI.Sprite(particleTextures.snow);
    const size = Math.random() * 8 + 4; // Size between 4 and 12
    snowflake.width = size;
    snowflake.height = size;
    snowflake.x = Math.random() * APP_WIDTH;
    snowflake.y = -20; // Start above screen
    snowflake.alpha = Math.min(0.2 + (size / 12) * 0.7, 0.8);

    // Add rotation and motion data
    (snowflake as any).userData = {
      rotationSpeed: (Math.random() - 0.5) * 0.05,
      speedX: (Math.random() - 0.5) * 0.7,
      speedY: Math.random() * 1.5 + 1.0,
      lifeTime: 0,
    };

    // Extra safety checks before adding to container
    if (!snowContainer || snowContainer.destroyed) {
      console.warn('Snow container was destroyed during particle creation');
      snowflake.destroy();
      return;
    }

    // Safely add to container with try/catch
    try {
      snowContainer.addChild(snowflake);
      snowParticles.push(snowflake);
    } catch (err) {
      notifyError('Error adding snow particle to container:', err);
      if (!snowflake.destroyed) {
        snowflake.destroy();
      }
    }
  } catch (error) {
    notifyError('Unexpected error in createSnowParticles:', error);
  }
};

// Create fire particles
const createFireParticles = (intensity: number) => {
  if (!particleTextures || !app) return;

  // Skip if particle textures aren't ready
  if (
    !particleTextures.fireBack ||
    !particleTextures.fireMid ||
    !particleTextures.fireFront
  ) {
    console.warn(
      'Fire particle textures not initialized - skipping particle creation'
    );
    return;
  }

  // Base number of particles
  const baseBatchSize = 0.01;
  const intensityMultiplier = 0.05;

  // Calculate batch size
  const batchSize = Math.ceil(
    baseBatchSize * (1 + intensity * intensityMultiplier)
  );

  // Create particles with different intensities for each layer
  createFireLayerParticles('back', Math.ceil(batchSize * 0.2), intensity);
  createFireLayerParticles('mid', Math.ceil(batchSize * 0.4), intensity);
  createFireLayerParticles('front', Math.ceil(batchSize * 0.3), intensity);
};

// Helper function to create fire layer particles
const createFireLayerParticles = (
  layer: 'back' | 'mid' | 'front',
  count: number,
  intensity: number
) => {
  if (!app) return;

  // Early validation of particle textures
  if (!particleTextures) {
    console.warn('Particle textures not initialized');
    return;
  }

  const containerMap = {
    back: fireBackContainer,
    mid: fireMidContainer,
    front: fireFrontContainer,
  };

  const textureMap = {
    back: particleTextures.fireBack || [],
    mid: particleTextures.fireMid || [],
    front: particleTextures.fireFront || [],
  };

  const container = containerMap[layer];
  const textures = textureMap[layer];

  // More verbose error handling
  if (!container) {
    console.warn(`${layer} fire container not initialized`);
    return;
  }

  if (!textures || !Array.isArray(textures) || textures.length === 0) {
    console.warn(`${layer} fire textures not properly initialized`);
    return;
  }

  // Check particle count limits
  const maxParticles = layer === 'mid' ? 30 : 20;
  if (container.children.length >= maxParticles) return;

  // Adjust layer multipliers
  let layerMultiplier = 1;
  switch (layer) {
    case 'back':
      layerMultiplier = 0.4;
      break;
    case 'mid':
      layerMultiplier = 0.8;
      break;
    case 'front':
      layerMultiplier = 1.0;
      break;
  }

  // Create particles
  const temperatureFactor = Math.min(1, 0.5 + (70 - temperature) / 100);
  const batchSize = Math.max(
    1,
    Math.floor(count * intensity * 0.4 * layerMultiplier * temperatureFactor)
  );

  // Limit batch size
  const actualBatchSize = Math.min(batchSize, 5);

  for (let i = 0; i < actualBatchSize; i++) {
    try {
      // Choose random texture from the layer's textures
      const textureIndex = Math.floor(Math.random() * textures.length);
      if (!textures[textureIndex]) {
        console.warn(
          `Invalid texture at index ${textureIndex} for ${layer} layer`
        );
        continue;
      }

      const particle = new PIXI.Sprite(textures[textureIndex]);
      particle.anchor.set(0.5);

      // Position
      const screenWidth = app.screen.width;
      particle.x = Math.random() * screenWidth * 0.9 + screenWidth * 0.05;
      particle.y = app.screen.height + 10;

      // Store the texture index
      (particle as any).colorIndex = textureIndex;

      // Scale and alpha based on layer
      let scale, alpha, speedY, speedX, lifespan;

      switch (layer) {
        case 'back':
          scale = (0.7 + Math.random() * 0.6) * 0.7;
          alpha = 0.6 + Math.random() * 0.3;
          speedY = (-1.0 - Math.random() * 0.8) / 3;
          speedX = (Math.random() - 0.5) * 0.3;
          lifespan = (150 + Math.random() * 50) * 2.0;
          break;
        case 'mid':
          scale = (0.6 + Math.random() * 0.5) * 0.7;
          alpha = 0.8 + Math.random() * 0.2;
          speedY = (-1.5 - Math.random() * 1.0) / 3;
          speedX = (Math.random() - 0.5) * 0.4;
          lifespan = (130 + Math.random() * 40) * 2.0;
          break;
        case 'front':
          scale = (0.5 + Math.random() * 0.4) * 0.7;
          alpha = 1.0;
          speedY = (-2.0 - Math.random() * 1.0) / 3;
          speedX = (Math.random() - 0.5) * 0.5;
          lifespan = (100 + Math.random() * 30) * 2.0;
          break;
      }

      particle.scale.set(scale);
      particle.alpha = alpha;

      // Store properties for updates
      (particle as any).speedX = speedX;
      (particle as any).speedY = speedY;
      (particle as any).life = 1.0;
      (particle as any).fadeRate = 1.0 / lifespan;
      (particle as any).age = 0;

      container.addChild(particle);

      const particlesArray =
        layer === 'back'
          ? fireBackParticles
          : layer === 'mid'
            ? fireMidParticles
            : fireFrontParticles;
      particlesArray.push(particle);
    } catch (error) {
      notifyError(`Error creating ${layer} particle:`, error);
    }
  }
};

// Update particles efficiently
const updateParticles = () => {
  // Update snow particles
  if (snowContainer && !snowContainer.destroyed && snowParticles.length > 0) {
    const maxToProcess = Math.min(snowParticles.length, 20);
    for (let i = 0; i < maxToProcess; i++) {
      if (snowParticles.length === 0) break;

      const index = (Date.now() + i) % snowParticles.length;
      if (index >= snowParticles.length) continue; // Safety check for out-of-bounds

      const snowflake = snowParticles[index];

      if (!snowflake || snowflake.destroyed || !(snowflake as any).userData)
        continue;

      // Move snowflake
      snowflake.x += (snowflake as any).userData.speedX;
      snowflake.y += (snowflake as any).userData.speedY;

      if ((snowflake as any).userData.lifeTime % 2 === 0) {
        snowflake.x +=
          Math.sin((snowflake as any).userData.lifeTime * 0.05) * 0.3;
      }

      if ((snowflake as any).userData.lifeTime % 3 === 0) {
        snowflake.rotation += (snowflake as any).userData.rotationSpeed;
      }

      (snowflake as any).userData.lifeTime += 1;

      // Remove if it goes off screen
      if (snowflake.y > APP_HEIGHT + 50) {
        try {
          if (
            snowContainer &&
            !snowContainer.destroyed &&
            snowContainer.children.includes(snowflake)
          ) {
            snowContainer.removeChild(snowflake);
          }

          if (index < snowParticles.length) {
            snowParticles.splice(index, 1);
          }

          if (!snowflake.destroyed) {
            snowflake.destroy();
          }
        } catch (err) {
          console.warn('Error removing snow particle:', err);
          // Clean up array anyway to prevent memory leaks
          if (index < snowParticles.length) {
            snowParticles.splice(index, 1);
          }
        }
      }
    }
  }

  // Update fire particles
  updateFireLayerParticlesOptimized(fireBackContainer, fireBackParticles);
  updateFireLayerParticlesOptimized(fireMidContainer, fireMidParticles);
  updateFireLayerParticlesOptimized(fireFrontContainer, fireFrontParticles);
};

// Optimized fire particle updates
const updateFireLayerParticlesOptimized = (
  container: PIXI.ParticleContainer | null,
  particles: PIXI.Sprite[]
) => {
  if (!container) return;

  // Process a limited number of particles per frame
  const maxToProcess = Math.min(particles.length, 15);
  for (let i = 0; i < maxToProcess; i++) {
    if (particles.length === 0) break;

    // Round-robin processing
    const index = (Date.now() + i) % particles.length;
    const particle = particles[index];

    if (!particle) {
      // Clean up array slot if particle is missing
      if (index < particles.length) {
        particles.splice(index, 1);
      }
      continue;
    }

    // Move particle
    particle.x += (particle as any).speedX;
    particle.y += (particle as any).speedY;

    // Add slight random movement occasionally
    if ((particle as any).age % 3 === 0) {
      particle.x += (Math.random() - 0.5) * 0.05;
    }

    // Increment age
    (particle as any).age += 1;

    // Update fade
    if ((particle as any).fadeRate) {
      (particle as any).life -= (particle as any).fadeRate;
      particle.alpha = Math.max(0, Math.min(1, (particle as any).life * 1.5));
    }

    // Remove if off-screen or faded out
    if (particle.y < -150 || (particle as any).life <= 0) {
      container.removeChild(particle);
      if (index < particles.length) {
        particles.splice(index, 1);
      }
      particle.destroy();
    }
  }
};

// Helper function to clear all particles - add checks for destroyed containers
const clearAllParticles = () => {
  // Clear snow particles
  if (snowContainer && !snowContainer.destroyed) {
    for (let i = snowParticles.length - 1; i >= 0; i--) {
      const particle = snowParticles[i];
      if (particle && !particle.destroyed) {
        try {
          snowContainer.removeChild(particle);
          particle.destroy();
        } catch (err) {
          console.warn('Error removing snow particle during cleanup:', err);
        }
      }
    }
    snowParticles = [];
  }

  // Clear fire particles - all layers with additional error handling
  [
    { container: fireBackContainer, particles: fireBackParticles },
    { container: fireMidContainer, particles: fireMidParticles },
    { container: fireFrontContainer, particles: fireFrontParticles },
  ].forEach(({ container, particles }) => {
    if (container && !container.destroyed) {
      for (let i = particles.length - 1; i >= 0; i--) {
        const particle = particles[i];
        if (particle && !particle.destroyed) {
          try {
            container.removeChild(particle);
            particle.destroy();
          } catch (err) {
            console.warn(`Error removing fire particle during cleanup:`, err);
          }
        }
      }
    }
    // Clear arrays regardless of container state
    if (particles === fireBackParticles) fireBackParticles = [];
    if (particles === fireMidParticles) fireMidParticles = [];
    if (particles === fireFrontParticles) fireFrontParticles = [];
  });
};

// Load textures implementation
const loadTextures = async () => {
  console.log('Loading button textures...');
  try {
    //////////////////////////////////////////////////////////////////
    // Strange bug fix for PIXI.Assets
    // on some device, there is a bug with PIXI.Assets.load, the first loading is not working.
    // so the need of this hack
    //////////////////////////////////////////////////////////////////
    try {
      await loadTextureWithFallback(
        assets.common.diamond,
        'png',
        app,
        0x00ffff,
        'circle',
        125
      );
    } catch (error) {
      console.warn('There is some "initialization" bug in PIXI.Assets', error);
    }
    //////////////////////////////////////////////////////////////////

    // Load diamond texture with fallback
    diamondTexture = await loadTextureWithFallback(
      assets.common.diamond,
      'png',
      app,
      0x00ffff,
      'circle',
      125
    );

    // Load flame texture with fallback
    flameTexture = await loadTextureWithFallback(
      assets.game_heater.images.button_hot,
      'png',
      app,
      0xff5500,
      'circle',
      125
    );
  } catch (error) {
    notifyError('Error loading game textures:', error);
  }

  if (!app || !app.renderer) {
    console.warn(
      'Skipping particle texture creation as renderer is not available'
    );
    return;
  }

  createParticleTextures();
};

// Create particle textures - add extra safeguards
const createParticleTextures = () => {
  try {
    if (!app || !app.renderer) {
      notifyError(
        'Cannot create particle textures: app or renderer not available'
      );
      return;
    }

    // Check if we're using canvas renderer and simplify operations
    const isCanvasRenderer = app.renderer.type === PIXI.RENDERER_TYPE.CANVAS;
    if (isCanvasRenderer) {
      console.log('Using canvas renderer - simplifying particle effects');
    }

    // Initialize texture arrays as empty arrays (not null)
    particleTextures.fireBack = [];
    particleTextures.fireMid = [];
    particleTextures.fireFront = [];

    // Create snow particle texture with error handling
    try {
      const snowGraphics = new PIXI.Graphics();
      snowGraphics.beginFill(0xffffff);
      snowGraphics.drawCircle(0, 0, 4);
      snowGraphics.endFill();
      particleTextures.snow = app.renderer.generateTexture(snowGraphics);
    } catch (err) {
      console.warn('Failed to create snow texture, using empty texture', err);
      particleTextures.snow = PIXI.Texture.EMPTY;
    }

    // If using canvas renderer, use simpler particle containers with fewer features
    const particleOptions = isCanvasRenderer
      ? { scale: true, position: true, alpha: true }
      : { scale: true, position: true, rotation: true, alpha: true };

    try {
      // Create containers for particles if they don't exist
      if (!snowContainer || snowContainer.destroyed) {
        snowContainer = new PIXI.ParticleContainer(
          isCanvasRenderer ? 100 : 200,
          particleOptions
        );
        if (app && app.stage && !app.stage.destroyed) {
          app.stage.addChild(snowContainer);
        } else {
          console.warn(
            'Could not add snow container to stage - stage not available'
          );
        }
      }

      if (!fireBackContainer || fireBackContainer.destroyed) {
        fireBackContainer = new PIXI.ParticleContainer(
          isCanvasRenderer ? 50 : 100,
          { scale: true, position: true, alpha: true }
        );
        if (app && app.stage && !app.stage.destroyed) {
          app.stage.addChild(fireBackContainer);
        }
      }

      if (!fireMidContainer || fireMidContainer.destroyed) {
        fireMidContainer = new PIXI.ParticleContainer(
          isCanvasRenderer ? 50 : 100,
          { scale: true, position: true, alpha: true }
        );
        if (app && app.stage && !app.stage.destroyed) {
          app.stage.addChild(fireMidContainer);
        }
      }

      if (!fireFrontContainer || fireFrontContainer.destroyed) {
        fireFrontContainer = new PIXI.ParticleContainer(
          isCanvasRenderer ? 50 : 100,
          { scale: true, position: true, alpha: true }
        );
        if (app && app.stage && !app.stage.destroyed) {
          app.stage.addChild(fireFrontContainer);
        }
      }
    } catch (err) {
      notifyError('Error creating particle containers:', err);
      // Set containers to null to prevent usage
      snowContainer = null;
      fireBackContainer = null;
      fireMidContainer = null;
      fireFrontContainer = null;
      return;
    }

    // Different color sets based on renderer type
    // Background layer - dark red particles
    const darkColors = [0x660000, 0x770000, 0x880000];

    // Middle layer - vibrant red particles
    const midColors = [0xa30000, 0xcc0000, 0xe50000, 0xff0000, 0xff2200];

    // Foreground layer - intense red particles
    const redColors = [0xe50000, 0xff0000, 0xff2200];

    // Generate textures for each layer
    try {
      particleTextures.fireBack = createFireTextures(darkColors, 0.8, 8);
      particleTextures.fireMid = createFireTextures(midColors, 0.9, 8);
      particleTextures.fireFront = createFireTextures(redColors, 1.0, 8);
    } catch (error) {
      notifyError('Error generating fire textures:', error);
      // Provide fallback textures if generation failed
      provideFallbackFireTextures();
    }

    // Validate that textures were created successfully
    if (!particleTextures.fireBack || particleTextures.fireBack.length === 0) {
      console.warn(
        'Fire back textures not created successfully, using fallbacks'
      );
      provideFallbackFireTextures();
    }
  } catch (error) {
    notifyError('Error creating particle textures:', error);
    provideFallbackFireTextures();
  }
};

// Create fallback fire textures if the normal creation fails
const provideFallbackFireTextures = () => {
  try {
    if (!app || !app.renderer) return;

    // Create simple fallback textures for each layer
    const backGraphics = new PIXI.Graphics();
    backGraphics.beginFill(0x660000, 0.8);
    backGraphics.drawCircle(0, 0, 8);
    backGraphics.endFill();

    const midGraphics = new PIXI.Graphics();
    midGraphics.beginFill(0xff0000, 0.9);
    midGraphics.drawCircle(0, 0, 8);
    midGraphics.endFill();

    const frontGraphics = new PIXI.Graphics();
    frontGraphics.beginFill(0xff5500, 1.0);
    frontGraphics.drawCircle(0, 0, 8);
    frontGraphics.endFill();

    // Set fallback textures (as arrays with single texture)
    particleTextures.fireBack = [app.renderer.generateTexture(backGraphics)];
    particleTextures.fireMid = [app.renderer.generateTexture(midGraphics)];
    particleTextures.fireFront = [app.renderer.generateTexture(frontGraphics)];

    console.log('Fallback fire textures created successfully');
  } catch (error) {
    notifyError('Failed to create even fallback textures:', error);
    // Initialize as empty arrays as last resort
    particleTextures.fireBack = [];
    particleTextures.fireMid = [];
    particleTextures.fireFront = [];
  }
};

// Helper to create fire textures
const createFireTextures = (colors: number[], alpha: number, size: number) => {
  try {
    return colors.map((color) => {
      const graphics = new PIXI.Graphics();
      graphics.beginFill(color, alpha);
      graphics.drawCircle(0, 0, size);
      graphics.endFill();
      return app.renderer.generateTexture(graphics);
    });
  } catch (error) {
    notifyError('Error in createFireTextures:', error);
    return [];
  }
};

// Start game timer
const startGameTimer = () => {
  // Clear any existing timer
  if (gameTimerInterval) {
    clearInterval(gameTimerInterval);
  }

  lastTimerUpdateTime = Date.now();

  // Start a new timer that decrements gameTimeLeftInSec every second
  gameTimerInterval = setInterval(() => {
    if (status !== 'playing') return;

    // Calculate actual elapsed time to account for timing inconsistencies
    const now = Date.now();
    const elapsedSec = Math.floor((now - lastTimerUpdateTime) / 1000);
    lastTimerUpdateTime = now;

    if (elapsedSec > 0) {
      gameTimeLeftInSec = Math.max(0, gameTimeLeftInSec - elapsedSec);

      // Notify callback
      if (onTimeUpdatedCallback) {
        onTimeUpdatedCallback(gameTimeLeftInSec);
      }

      // Check if time is up
      if (gameTimeLeftInSec <= 0) {
        handleTimeUp();
      }
    }
  }, 1000);
};

// Handle time up
const handleTimeUp = () => {
  // Stop the timer
  if (gameTimerInterval) {
    clearInterval(gameTimerInterval);
  }

  // End the game with timeout reason
  endGame('timeout');
};

// Pause the game
const pauseGame = () => {
  if (status !== 'playing') return;

  // Save the previous status to resume later
  setStatus('paused');

  // Pause the PIXI ticker
  if (app && app.ticker) {
    app.ticker.stop();
  }

  // Pause any active sounds
  getAllSounds().forEach((sound) => {
    if (sound && sound.playing()) {
      sound.pause();
    }
  });

  // Pause GSAP animations
  gsap.globalTimeline.pause();

  // Clear the button spawn timer
  if (buttonSpawnTimer) {
    clearTimeout(buttonSpawnTimer);
    buttonSpawnTimer = null as unknown as ReturnType<typeof setTimeout>;
  }

  console.log('Game paused');
};

// Resume the game
const resumeGame = () => {
  if (status !== 'paused') return;

  setStatus('playing');

  // Resume the PIXI ticker
  if (app && app.ticker) {
    app.ticker.start();
  }

  // Resume GSAP animations
  gsap.globalTimeline.resume();

  // Reset timer reference time
  lastTimerUpdateTime = Date.now();

  // Restart button spawning if needed
  if (app && app.stage && !buttonSpawnTimer) {
    console.log('Restarting button spawning...');
    buttonSpawnTimer = setTimeout(spawnButtons, 500);
  }

  console.log('Game resumed');
};

// --------------------- exports ------------------------

export const GameHeater = {
  initApp,
  updateCallbacks,
  startGame,
  destroyApp,
  getStatus,
  pauseGame,
  resumeGame,
  convertTemperatureForDisplay,
  TEMPERATURE_EXTREMELY_COLD: GAME_CONFIG.TEMPERATURE_EXTREMELY_COLD,
  TEMPERATURE_COLD: GAME_CONFIG.TEMPERATURE_COLD,
  TEMPERATURE_HOT: GAME_CONFIG.TEMPERATURE_HOT,
  TEMPERATURE_DECREASE_RATE: GAME_CONFIG.TEMPERATURE_DECREASE_RATE,
  TEMPERATURE_DECREASE_RATE_ACCELERATION:
    GAME_CONFIG.TEMPERATURE_DECREASE_RATE_ACCELERATION,
  MAX_TEMPERATURE_DECREASE_RATE: GAME_CONFIG.MAX_TEMPERATURE_DECREASE_RATE,
  BUBBLE_SPAWN_RATE_ACCELERATION: GAME_CONFIG.BUBBLE_SPAWN_RATE_ACCELERATION,
  MAX_BUBBLE_SPAWN_RATE: GAME_CONFIG.MAX_BUBBLE_SPAWN_RATE,
};
